import { useState, useEffect } from "react";
import {
  Plus, Trash2, Phone, Bike, ToggleLeft, ToggleRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API } from "@/lib/api";

export const RiderMaster = () => {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const fetchRiders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/riders`);
      if (!res.ok) throw new Error("Server error");
      setRiders(await res.json());
    } catch (e) { toast.error("Failed to load riders: " + (e.message || "check backend")); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRiders(); }, []);

  const handleSave = async () => {
    if (!form.full_name.trim()) { toast.error("Name is required"); return; }
    if (!form.phone.trim()) { toast.error("Phone is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/riders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) { let msg = "Server error"; try { const d = await res.json(); msg = d.detail || msg; } catch {} throw new Error(msg); }
      toast.success("Rider added!");
      setDialogOpen(false);
      setForm({ full_name: "", phone: "" });
      fetchRiders();
    } catch (e) { toast.error(e.message || "Failed to add rider"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API}/riders/${selected.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      toast.success("Rider deleted");
      setDeleteDialog(false);
      setSelected(null);
      fetchRiders();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleActive = async (rider) => {
    try {
      const res = await fetch(`${API}/riders/${rider.id}/toggle-active`, { method: "PATCH" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`${rider.full_name} is now ${data.is_active ? "active" : "inactive"}`);
      fetchRiders();
    } catch { toast.error("Failed to toggle"); }
  };

  const activeCount = riders.filter(r => r.is_active).length;

  return (
    <div className="space-y-6" data-testid="rider-master-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Rider Master</h1>
          <p className="text-slate-500 text-sm mt-0.5">{riders.length} rider{riders.length !== 1 ? "s" : ""} &middot; {activeCount} active</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#0047FF] hover:bg-[#0033CC]">
          <Plus className="w-4 h-4 mr-2" /> Add Rider
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-pulse text-slate-500">Loading riders...</div></div>
      ) : riders.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {riders.map((rider) => (
            <Card key={rider.id} className={`border-slate-200 ${rider.is_active ? "" : "opacity-60"}`}>
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${rider.is_active ? "bg-emerald-100" : "bg-slate-100"}`}>
                      <Bike className={`w-5 h-5 ${rider.is_active ? "text-emerald-600" : "text-slate-400"}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{rider.full_name}</CardTitle>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-0.5">
                        <Phone className="w-3.5 h-3.5" /> {rider.phone}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { setSelected(rider); setDeleteDialog(true); }} className="h-8 w-8 text-slate-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleActive(rider)}
                  className={`w-full ${rider.is_active ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-slate-300 text-slate-500"}`}
                >
                  {rider.is_active ? <ToggleRight className="w-4 h-4 mr-2 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 mr-2" />}
                  {rider.is_active ? "Active" : "Inactive"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Bike className="w-14 h-14 text-slate-200 mx-auto mb-4" />
            <h3 className="text-base font-medium text-slate-600 mb-2">No riders yet</h3>
            <p className="text-slate-400 text-sm mb-6">Add riders to start assigning deliveries</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#0047FF] hover:bg-[#0033CC]">
              <Plus className="w-4 h-4 mr-2" /> Add Rider
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Rider Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add New Rider</DialogTitle>
            <DialogDescription>Enter rider details. They'll use their phone number to log in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <Label>Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => setForm(p => ({ ...p, full_name: e.target.value }))} placeholder="e.g., Amit Kumar" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone Number *</Label>
              <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="e.g., 9100000001" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0047FF] hover:bg-[#0033CC]">{saving ? "Adding..." : "Add Rider"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rider</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{selected?.full_name}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
